import { trpc } from "@/lib/trpc";

// Registrar o Service Worker sem reutilizar uma cópia antiga do arquivo de controle.
// Isso evita que uma versão anterior do formulário continue servindo bundles desatualizados.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then(async (registration) => {
        await registration.update();
        if (registration.waiting) registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      })
      .catch(() => {});
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      const reloadKey = 'bb_sw_cache_v8_reloaded';
      if (!window.sessionStorage.getItem(reloadKey)) {
        window.sessionStorage.setItem(reloadKey, 'true');
        window.location.reload();
      }
    });
  });
}
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { FeaturesProvider } from "./contexts/FeaturesContext";
import { SalonProvider } from "./contexts/SalonContext";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Não tentar novamente em erros de autenticação — evita loop de loading
      retry: (failureCount, error) => {
        if (error instanceof TRPCClientError) {
          const code = (error as TRPCClientError<any>).data?.code;
          // Sem retry para erros de auth ou not found
          if (code === 'UNAUTHORIZED' || code === 'FORBIDDEN' || code === 'NOT_FOUND') return false;
        }
        // Máximo 1 retry para outros erros (padrão era 3)
        return failureCount < 1;
      },
      // Cache por 30s — evita re-fetch desnecessário ao trocar de aba
      staleTime: 30_000,
      // Não refazer query ao focar a janela — evita spike de conexões no banco
      refetchOnWindowFocus: false,
      // Garbage collect após 5 minutos
      gcTime: 5 * 60_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    queryClient.invalidateQueries();
  });
}

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  // Profissionais que já usaram o acesso interno voltam para a mesma entrada
  // quando a sessão expira; não devem ser enviados ao login administrativo.
  const currentPath = window.location.pathname;
  const isLoginOrPublicPath = currentPath.startsWith('/login') || currentPath.startsWith('/team-login') || currentPath.startsWith('/booking') || currentPath.startsWith('/s/') || currentPath.startsWith('/filial/') || currentPath.startsWith('/fila/');
  if (!isLoginOrPublicPath) {
    const accessMode = window.localStorage.getItem("bb_access_mode");
    window.location.href = accessMode === "team" ? "/team-login" : "/login";
  }
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        if (typeof navigator !== "undefined" && !navigator.onLine) {
          return Promise.reject(new Error("Sem conexão. A ação não foi enviada; reconecte e tente novamente."));
        }
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <SalonProvider>
        <FeaturesProvider>
          <App />
        </FeaturesProvider>
      </SalonProvider>
    </QueryClientProvider>
  </trpc.Provider>
);
